import { useEffect, useRef, useState } from 'react'
import type { User } from 'oidc-client-ts'
import { clearAuthTokenCookies, setAuthTokenToCookies } from 'api/auth'
import { reportClientError } from 'services/errorReportingService'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { ACCOUNT_INIT_COMPLETE, SET_ACCESS_TOKEN } from 'actions/actionConstants'
import { registerAuthTokenRefreshHandler, unregisterAuthTokenRefreshHandler } from 'api/authTokenRefresh'
import { ensureValidOidcSession, getOidcUserManager } from './ensureValidOidcSession'
import { recoverOidcSession } from './recoverOidcSession'
import { clearAuthMethodMarker, isOidcSession } from './authMethod'

// App-wide coordinator for a v2 (OIDC) session: recovers an SSR-anonymous session
// (returning true while it does, for a blocking overlay), refreshes on foreground
// and on a 401 (via the registered handler), and mirrors each fresh token into both
// the SSR cookie and Redux — tokenLoader reads the client bearer from Redux, not the
// cookie. No-op for v1.
export default function useOidcSessionCoordinator(): boolean {
  const [isMounted, setIsMounted] = useState(false)
  const [isRestoringSession, setIsRestoringSession] = useState(false)
  const dispatch = useAppDispatch()
  // A ref so the effects don't re-run (re-creating listeners) when login flips true.
  const loggedInRef = useRef(false)
  loggedInRef.current = useAppSelector(selectLoggedIn)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (!isMounted || !isOidcSession()) {
      return
    }

    let disposed = false
    let detach: () => void = () => undefined
    registerAuthTokenRefreshHandler(ensureValidOidcSession)

    const mirrorToken = (user: User) => {
      if (user.access_token) {
        setAuthTokenToCookies(user.access_token)
        dispatch({ type: SET_ACCESS_TOKEN, accessToken: user.access_token })
      }
    }

    const onSilentRenewError = (error: Error) => {
      reportClientError(error, { context: 'oidcSilentRenew' }).catch(() => undefined)
    }

    const setup = async() => {
      try {
        const userManager = await getOidcUserManager()
        if (disposed) {
          return
        }
        userManager.events.addUserLoaded(mirrorToken)
        userManager.events.addSilentRenewError(onSilentRenewError)
        detach = () => {
          userManager.events.removeUserLoaded(mirrorToken)
          userManager.events.removeSilentRenewError(onSilentRenewError)
        }

        const user = await userManager.getUser()
        if (user) {
          mirrorToken(user)
        }

        if (loggedInRef.current) {
          return
        }

        setIsRestoringSession(true)
        try {
          await dispatch(recoverOidcSession())
        } catch(error) {
          // Refresh token dead/revoked: clear the session so the next load logs out
          // cleanly instead of retrying this doomed restore forever.
          reportClientError(error, { context: 'oidcSessionRecover' }).catch(() => undefined)
          await userManager.removeUser().catch(() => undefined)
          clearAuthMethodMarker()
          clearAuthTokenCookies()
          dispatch({ type: ACCOUNT_INIT_COMPLETE })
        } finally {
          if (!disposed) {
            setIsRestoringSession(false)
          }
        }
      } catch(error) {
        // Setup failed. initialiseUser defers init for the OIDC path, so settle it
        // here or the app hangs uninitialised.
        reportClientError(error, { context: 'oidcSessionCoordinator' }).catch(() => undefined)
        if (!disposed) {
          setIsRestoringSession(false)
        }
        dispatch({ type: ACCOUNT_INIT_COMPLETE })
      }
    }

    setup()

    return () => {
      disposed = true
      detach()
      unregisterAuthTokenRefreshHandler(ensureValidOidcSession)
    }
  }, [isMounted, dispatch])

  // Foreground renew for a returning tab; no-ops when the token is still fresh.
  // visibilitychange covers hidden -> visible (including wake from sleep) and
  // pageshow covers a bfcache restore; focus would only add the visible-but-
  // unfocused case, where timers never stopped and automaticSilentRenew applies.
  useEffect(() => {
    if (!isMounted || !isOidcSession()) {
      return
    }

    const onForeground = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return
      }
      // Bootstrap owns the logged-out case.
      if (!loggedInRef.current) {
        return
      }
      ensureValidOidcSession().catch((error) =>
        reportClientError(error, { context: 'oidcForegroundRenew' }).catch(() => undefined),
      )
    }

    document.addEventListener('visibilitychange', onForeground)
    window.addEventListener('pageshow', onForeground)

    return () => {
      document.removeEventListener('visibilitychange', onForeground)
      window.removeEventListener('pageshow', onForeground)
    }
  }, [isMounted])

  return isRestoringSession
}
